/**
 * Product Visibility Tracker for Google Analytics Enhanced Ecommerce
 * Відстежує товари, які потрапляють у видиму область екрана
 */
class ProductVisibilityTracker {
    constructor() {
        this.observer = null;
        this.visibleProducts = new Set(); // Зберігає ID товарів, які вже були відстежені
        this.pendingProducts = new Map(); // Зберігає дані товарів, які очікують на відстеження
        this.listContext = null; // Контекст списку товарів (назва та ID)
        this.init();
    }
    init() {
        // Ініціалізуємо Intersection Observer
        if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver(
                this.handleIntersection.bind(this),
                {
                    root: null, // viewport
                    rootMargin: '0px',
                    threshold: 1.0 // Товар повністю видимий
                }
            );
        } else {
            console.error('[GA] init: IntersectionObserver не підтримується в цьому браузері');
        }
    }
    /**
     * Встановлює контекст списку товарів
     * @param {string} listName - Назва списку товарів
     * @param {string} listId - ID списку товарів
     */
    setListContext(listName, listId) {
        this.listContext = {
            name: listName,
            id: listId
        };
    }
    /**
     * Додає контейнер slider-products__wrapper для відстеження
     * @param {HTMLElement} containerElement - DOM елемент контейнера
     * @param {Array} products - Масив товарів
     * @param {string} listName - Назва списку товарів
     * @param {string} listId - ID списку товарів
     */
    addSliderContainer(containerElement, products, listName = 'Slider Products', listId = 'slider-products') {
        if (!this.observer || !containerElement) {
            console.warn('[GA] addSliderContainer: Observer не ініціалізований або контейнер відсутній');
            return;
        }
        if (!products || !Array.isArray(products) || products.length === 0) {
            console.warn('[GA] addSliderContainer: Невірні дані товарів:', products);
            return;
        }
        
        // Встановлюємо контекст списку
        this.setListContext(listName, listId);
        // Додаємо data-атрибути для ідентифікації
        containerElement.setAttribute('data-slider-container', 'true');
        containerElement.setAttribute('data-slider-tracked', 'false');
        containerElement.setAttribute('data-slider-products-count', products.length);
        // Зберігаємо дані товарів для цього контейнера
        const containerId = `slider-${Date.now()}`;
        this.pendingProducts.set(containerId, {
            containerId,
            products,
            listName,
            listId,
            isSlider: true
        });
        // Починаємо відстеження контейнера
        this.observer.observe(containerElement);
        
    }
    /**
     * Додає товар для відстеження
     * @param {HTMLElement} element - DOM елемент товару
     * @param {Object} productData - Дані товару
     */
    addProduct(element, productData) {
        if (!this.observer || !element) {
            console.warn('[GA] Observer не ініціалізований або елемент відсутній');
            return;
        }
        const productId = this.extractProductId(productData);
        if (!productData) {
            console.warn('[GA] Невірні дані товару:', productData);
            return;
        }
        if (productId === '' || productId === null || productId === undefined) {
            console.warn('[GA] Пропускаємо товар без item_id у visibility tracker:', productData);
            return;
        }
        
        
        // Доповнюємо дані товару інформацією з DOM, якщо вона відсутня
        const enhancedProductData = {
            ...productData,
            category: productData.category || this.extractCategoryFromDOM(element),
            productBrand: productData.productBrand || this.extractBrandFromDOM(element)
        };
        
        
        // Додаємо data-атрибути для ідентифікації
        // Використовуємо вже визначений productId (enhancedProductData містить ті самі дані)
        element.setAttribute('data-product-id', String(productId));
        element.setAttribute('data-product-tracked', 'false');
        // Зберігаємо дані товару (як рядок для сумісності з DOM)
        this.pendingProducts.set(String(productId), enhancedProductData);
        // Починаємо відстеження
        this.observer.observe(element);
        
    }
    /**
     * Обробляє зміни видимості товарів
     * @param {IntersectionObserverEntry[]} entries
     */
    handleIntersection(entries) {

        const visibleProducts = [];
        entries.forEach(entry => {
            if (entry.isIntersecting) {
                // Перевіряємо, чи це slider контейнер
                const isSliderContainer = entry.target.getAttribute('data-slider-container') === 'true';
                const isSliderTracked = entry.target.getAttribute('data-slider-tracked') === 'true';
                
                if (isSliderContainer && !isSliderTracked) {
                    // Позначаємо контейнер як відстежений
                    entry.target.setAttribute('data-slider-tracked', 'true');
                    // Знаходимо дані контейнера
                    const containerData = Array.from(this.pendingProducts.values())
                        .find(data => data.isSlider && data.containerId);
                    if (containerData && containerData.products) {
                        // Форматуємо всі товари з контейнера
                        const formattedProducts = containerData.products.map(product => {
                            return this.formatProductData(product);
                        });
                        visibleProducts.push(...formattedProducts);
                        // Видаляємо дані контейнера з pending
                        this.pendingProducts.delete(containerData.containerId);
                    } else {
                        console.warn('[GA] Дані slider контейнера не знайдені');
                    }
                } else {
                    // Звичайна обробка окремих товарів
                    const productId = entry.target.getAttribute('data-product-id');
                    const isTracked = entry.target.getAttribute('data-product-tracked') === 'true';
                    
                    if (productId && !isTracked && !this.visibleProducts.has(productId)) {
                        // Позначаємо товар як відстежений
                        entry.target.setAttribute('data-product-tracked', 'true');
                        this.visibleProducts.add(productId);
                        // Додаємо до списку для відправки (productId вже рядок з DOM)
                        const productData = this.pendingProducts.get(productId);
                        if (productData) {
                            const formattedProduct = this.formatProductData(productData);
                            visibleProducts.push(formattedProduct);
                        } else {
                            console.warn(`[GA] Дані товару ${productId} не знайдені в pendingProducts`);
                        }
                    }
                }
            }
        });
        
        // Відправляємо дані, якщо є видимі товари
        if (visibleProducts.length > 0) {
            this.sendToDataLayer(visibleProducts);
            // Зберігаємо категорію останнього товару в sessionStorage для подальшого використання
            this.saveLastCategoryToSessionStorage(visibleProducts);
        }
    }
    /**
     * Форматує дані товару для Google Analytics
     * @param {Object} productData - Дані товару
     * @returns {Object}
     */
    formatProductData(productData) {
        const price = this.extractPrice(productData);
        
        // Категорія фолбек: спочатку з роуту (каталогу), потім з sessionStorage
        let categoryOverride = '';
        // Спочатку спробуємо отримати з роуту (каталогу)
        const routeCategory = this.getCurrentCategoryFromRoute();
        if (routeCategory) {
            const categoryName = this.getCategoryNameFromCatalog(routeCategory);
            if (categoryName) {
                categoryOverride = categoryName;
            }
        }
        
        // Якщо не знайшли в роуті, спробуємо sessionStorage
        if (!categoryOverride) {
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    categoryOverride = sessionStorage.getItem('ga_last_item_category') || '';
                }
            } catch (e) { /* no-op */ }
        }
        
        // Визначаємо назву товару - обов'язково має бути назва, а не ID
        const productId = this.extractProductId(productData);
        let itemName = productData.title || productData.name || '';
        if (!itemName && productId) {
            itemName = `Товар ${productId}`;
        }
        
        // Визначаємо бренд
        let finalBrand = productData.productBrand?.title || 
                        productData.brand?.title || 
                        productData.manufacturer?.title || '';
        
        if (!finalBrand && itemName) {
            finalBrand = this.extractBrandFromTitle(itemName);
        }
        
        // Визначаємо категорію та її ієрархію
        const categoryData = this.extractCategoryHierarchy(productData, categoryOverride);
        
        const formattedProduct = {
            item_name: itemName,
            item_id: this.normalizeId(productId),
            price: price,
            currency: 'UAH',
            item_brand: finalBrand || 'Невизначений бренд',
            item_category: categoryData.category,
            // Додаткові категорії не потрібні для view_item_list
            // item_category2: categoryData.category2,
            // item_category3: categoryData.category3,
            // item_category4: categoryData.category4,
            // item_category5: categoryData.category5,
        };
        
        return formattedProduct;
    }
    /**
     * Витягує ціну з даних товару
     * @param {Object} productData - Дані товару
     * @returns {number}
     */
    extractPrice(productData) {
        if (productData.productPrice) {
            const priceData = productData.productPrice;
            // Завжди використовуємо ціну за лист/штуку, а не за M2
            if (priceData.baseUnit?.isM2) {
                return parseFloat(priceData.price?.price || 0);
            } else {
                return parseFloat(priceData.price?.price || 0);
            }
        }
        return 0;
    }
    /**
     * Отримує поточну категорію з роуту
     * @returns {string}
     */
    getCurrentCategoryFromRoute() {
        try {
            // Перевіряємо, чи є Vue router доступний
            if (typeof window !== 'undefined' && window.$nuxt) {
                const route = window.$nuxt.$route;
                if (route && route.params && route.params.slug) {
                    return route.params.slug;
                }
            }
            // Альтернативний спосіб - через URL
            const path = window.location.pathname;
            const pathParts = path.split('/');
            const katalogIndex = pathParts.findIndex(part => part === 'katalog');
            if (katalogIndex !== -1 && pathParts[katalogIndex + 1]) {
                const slug = pathParts[katalogIndex + 1];
                return slug;
            }
            return '';
        } catch (e) {
            console.warn('Помилка отримання категорії з роуту:', e);
            return '';
        }
    }

    /**
     * Отримує назву категорії з каталогу за slug
     * @param {string} slug - Slug категорії
     * @returns {string}
     */
    getCategoryNameFromCatalog(slug) {
        if (!slug) {
            return '';
        }
        // Список каталогів з футера (правильні slug з бази даних)
        const catalogMap = {
            'monolitnii-polikarbonat': 'Монолітний полікарбонат',
            'profilyovanii-polikarbonat': 'Профільований полікарбонат',
            'sotovii-polikarbonat': 'Сотовий полікарбонат',
            'vstavka-do-pokrivli-iz-sendvich-paneli': 'Вставка до покрівлі із сендвіч панелі',
            'svitloprozori-fasadni-systemy': 'Світлопрозорі фасадні системи',
            'svitloprozori-pokrivelni-systemy': 'Світлопрозорі покрівельні системи',
            'aksessuary': 'Аксесуари',
            'rishennya-dlya-avtomobiliv': 'Рішення для автомобілів',
            'agroteplitsi': 'Агротеплиці'
        };
        const categoryName = catalogMap[slug];
        if (categoryName) {
            return categoryName;
        } else {
            return slug;
        }
    }
    /**
     * Витягує категорію з DOM елемента
     * @param {HTMLElement} element - Елемент товару
     * @returns {Object}
     */
    extractCategoryFromDOM(element) {
        // Спочатку перевіряємо data-атрибут
        const dataCategory = element.getAttribute('data-product-category');
        if (dataCategory && dataCategory.trim()) {
            return {name: dataCategory.trim()};
        }
        // Отримуємо категорію з роуту (каталогу)
        const routeCategory = this.getCurrentCategoryFromRoute();
        if (routeCategory) {
            const categoryName = this.getCategoryNameFromCatalog(routeCategory);
            if (categoryName) {
                return {name: categoryName};
            }
        }
        // Шукаємо елементи з категорією
        const categoryElements = element.querySelectorAll('[class*="category"], [class*="Category"]');
        for (const catEl of categoryElements) {
            const categoryText = catEl.textContent || catEl.innerText;
            if (categoryText.trim()) {
                return {
                    name: categoryText.trim()
                };
            }
        }
        // Фолбек: беремо категорію зі сторінки каталогу, якщо вона задана
        const pageCategory = document.querySelector('[data-current-category]')?.getAttribute('data-current-category');
        if (pageCategory && pageCategory.trim()) {
            return {name: pageCategory.trim()};
        }
        return {name: 'Невизначена категорія'};
    }
    /**
     * Витягує бренд з DOM елемента
     * @param {HTMLElement} element - Елемент товару
     * @returns {Object}
     */
    extractBrandFromDOM(element) {
        // Шукаємо елементи з брендом
        const brandElements = element.querySelectorAll('[class*="brand"], [class*="Brand"]');
        for (const brandEl of brandElements) {
            const brandText = brandEl.textContent || brandEl.innerText;
            if (brandText.trim()) {
                return {
                    title: brandText.trim()
                };
            }
        }
        // Спробуємо витягнути бренд з title за евристикою (RODECA, BAUGLAS, BauGlas тощо)
        const titleText = element.getAttribute('data-product-title') || '';
        const brandFromTitle = this.extractBrandFromTitle(titleText);
        if (brandFromTitle) return {title: brandFromTitle};
        return {title: 'Невизначений бренд'};
    }
    /**
     * Витягує бренд з заголовка товару
     * @param {string} title - Заголовок товару
     * @returns {string}
     */
    extractBrandFromTitle(title) {
        if (!title) return '';
        const parseBrandFromTitle = (title) => {
            const t = (title || '').trim();
            if (!t) return '';
            const commaIdx = t.indexOf(',');
            if (commaIdx > 0) {
                const beforeComma = t.slice(0, commaIdx).trim();
                if (beforeComma.length >= 2 && beforeComma.length <= 60) return beforeComma;
            }
            const capsMatch = t.match(/\b[A-ZА-ЯЇІЄҐ]{3,}\b/u);
            if (capsMatch && capsMatch[0]) return capsMatch[0];
            const camelMatch = t.match(/\b[A-Z][a-z]+[A-Z][a-zA-Z]+\b/);
            if (camelMatch && camelMatch[0]) return camelMatch[0];
            const firstWord = t.match(/\b[\p{L}]{3,}\b/u);
            if (firstWord && firstWord[0]) return firstWord[0];
            return '';
        };
        return parseBrandFromTitle(title);
    }
    /**
     * Витягує ієрархію категорій
     * @param {Object} productData - Дані товару
     * @param {string} categoryOverride - Перевизначена категорія
     * @returns {Object}
     */
    extractCategoryHierarchy(productData, categoryOverride = '') {
        let category = categoryOverride || productData.category?.name || '';
        let category2 = '';
        let category3 = '';
        let category4 = '';
        let category5 = '';
        // Якщо є повна ієрархія категорій
        if (productData.category) {
            const cat = productData.category;
            category = cat.name || category;
            category2 = cat.parent?.name || '';
            category3 = cat.parent?.parent?.name || '';
            category4 = cat.parent?.parent?.parent?.name || '';
            category5 = cat.parent?.parent?.parent?.parent?.name || '';
        }
        // Якщо категорія все ще порожня, спробуємо отримати з роуту (каталогу)
        if (!category) {
            const routeCategory = this.getCurrentCategoryFromRoute();
            if (routeCategory) {
                const categoryName = this.getCategoryNameFromCatalog(routeCategory);
                if (categoryName) {
                    category = categoryName;
                }
            }
        }
        // Якщо категорія все ще порожня, спробуємо збережені дані
        if (!category) {
            try {
                if (typeof window !== 'undefined' && window.sessionStorage) {
                    const savedCategory = sessionStorage.getItem('ga_last_item_category');
                    if (savedCategory) {
                        category = savedCategory;
                    }
                }
            } catch (e) { /* no-op */ }
        }
        // Fallback - використовуємо назву товару
        if (!category && productData.title) {
            const words = productData.title.split(' ');
            if (words.length > 0) {
                category = words[0];
            }
        }
        return {
            category: category || 'Невизначена категорія',
            category2: category2,
            category3: category3,
            category4: category4,
            category5: category5
        };
    }
    /**
     * Витягує правильний ID товару з пріоритетом product_price_id
     * @param {Object} productData - Дані товару
     * @returns {any} ID товару
     */
    extractProductId(productData) {
        if (!productData) {
            return '';
        }
        
        // Пріоритет 1: productPrice.price.productPriceId (camelCase - основне місце з API)
        const priceProductPriceId = productData.productPrice?.price?.productPriceId;
        if (priceProductPriceId !== null && priceProductPriceId !== undefined) {
            return priceProductPriceId;
        }
        
        // Пріоритет 2: productPrice.priceOld.productPriceId (camelCase - якщо є знижка)
        const priceOldProductPriceId = productData.productPrice?.priceOld?.productPriceId;
        if (priceOldProductPriceId !== null && priceOldProductPriceId !== undefined) {
            return priceOldProductPriceId;
        }
        
        // Пріоритет 3: productPrice.price.product_price_id (snake_case fallback)
        const snakeCasePriceId = productData.productPrice?.price?.product_price_id;
        if (snakeCasePriceId !== null && snakeCasePriceId !== undefined) {
            return snakeCasePriceId;
        }
        
        // Пріоритет 4: productPrice.price_old.product_price_id (snake_case fallback)
        const snakeCasePriceOldId = productData.productPrice?.price_old?.product_price_id;
        if (snakeCasePriceOldId !== null && snakeCasePriceOldId !== undefined) {
            return snakeCasePriceOldId;
        }
        
        // Пріоритет 5: product_price_id на верхньому рівні об'єкта (fallback)
        const topLevelProductPriceId = productData.product_price_id;
        if (topLevelProductPriceId !== null && topLevelProductPriceId !== undefined) {
            return topLevelProductPriceId;
        }

        // Пріоритет 6: productPriceId на верхньому рівні об'єкта
        const topLevelProductPriceIdCamel = productData.productPriceId;
        if (topLevelProductPriceIdCamel !== null && topLevelProductPriceIdCamel !== undefined) {
            return topLevelProductPriceIdCamel;
        }

        // Пріоритет 7: ID товару як останній fallback
        const fallbackProductId = productData.id ?? productData.product?.id ?? productData.select_type?.id;
        if (fallbackProductId !== null && fallbackProductId !== undefined && fallbackProductId !== '') {
            return fallbackProductId;
        }
        
        // Якщо product_price_id не знайдено - повертаємо порожній рядок (порожня колонка)
        return '';
    }
    /**
     * Нормалізує ID до числа, якщо можливо, або повертає рядок/порожнє значення
     * (ідентично productClickTracker)
     */
    normalizeId(id) {
        if (id === null || id === undefined) return '';
        const num = Number(id);
        if (!Number.isNaN(num) && Number.isFinite(num)) return num;
        return ('' + id);
    }
    /**
     * Зберігає категорію останнього товару в sessionStorage
     * @param {Array} products - Масив товарів
     */
    saveLastCategoryToSessionStorage(products) {
        try {
            if (typeof window !== 'undefined' && window.sessionStorage && products.length > 0) {
                const lastProduct = products[products.length - 1];
                
                if (lastProduct.item_category) {
                    sessionStorage.setItem('ga_last_item_category', lastProduct.item_category);
                }
                if (this.listContext?.name) {
                    sessionStorage.setItem('ga_last_item_list_name', this.listContext.name);
                }
                if (this.listContext?.id) {
                    sessionStorage.setItem('ga_last_item_list_id', String(this.listContext.id));
                }
            } else {
                console.warn('[GA] saveLastCategoryToSessionStorage: sessionStorage недоступний або немає товарів');
            }
        } catch (e) {
            console.warn('[GA] saveLastCategoryToSessionStorage: Помилка збереження в sessionStorage:', e);
        }
    }
    /**
     * Відправляє дані у dataLayer Google Analytics
     * @param {Array} products - Масив товарів
     */
    sendToDataLayer(products) {
        if (!products || products.length === 0) {
            return;
        }

        // Відправляємо тільки через gtag/dataLayer.
        // Прямі запити до google-analytics.com часто блокуються adblock-розширеннями (ERR_BLOCKED_BY_CLIENT).
        if (typeof gtag !== 'undefined') {
            products.forEach((product, index) => {
                const gtagData = {
                    event_category: 'ecommerce',
                    event_label: this.listContext?.name || 'Product List',
                    item_list_name: this.listContext?.name || 'Product List',
                    item_list_id: this.listContext?.id || 'product-list',
                    items: [product]
                };
                gtag('event', 'view_item_list', gtagData);
            });
        }
        
        // Fallback до dataLayer
        if (window.dataLayer) {
            // Очищуємо попередні дані ecommerce
            window.dataLayer.push({ecommerce: null});
            const eventData = {
                event: 'view_item_list',
                ecommerce: { 
                    items: products 
                }
            };
            if (this.listContext) {
                eventData.item_list_name = this.listContext.name;
                eventData.item_list_id = this.listContext.id;
            }
            window.dataLayer.push(eventData);
            
            // Перевіряємо, чи всі обов'язкові поля заповнені
            const validationErrors = [];
            products.forEach((product, index) => {
                if (!product.item_name || product.item_name === product.item_id) {
                    validationErrors.push(`Товар ${index + 1}: item_name некоректний (${product.item_name})`);
                }
                if (!product.item_brand || product.item_brand === '') {
                    validationErrors.push(`Товар ${index + 1}: item_brand порожній`);
                }
                if (!product.item_category || product.item_category === '') {
                    validationErrors.push(`Товар ${index + 1}: item_category порожній`);
                }
                if (!product.price || product.price <= 0) {
                    validationErrors.push(`Товар ${index + 1}: price некоректний (${product.price})`);
                }
            });
            if (validationErrors.length > 0) {
                console.warn('[GA] Проблеми з даними товарів:', validationErrors);
            }
        } else {
            console.warn('[GA] dataLayer не доступний');
        }
    }
    // sendDirectToGA intentionally removed: use gtag/dataLayer only.
    /**
     * Очищає відстеження для нової сторінки
     */
    reset() {
        this.visibleProducts.clear();
        this.pendingProducts.clear();
        this.listContext = null;
        if (this.observer) {
            this.observer.disconnect();
        }
    }
    /**
     * Відстежує всі товари на сторінці (для тестування)
     */
    trackAllVisibleProducts() {
        const allProductElements = document.querySelectorAll('[data-product-id]');
        
        const visibleProducts = [];
        allProductElements.forEach((element, index) => {
            const productId = element.getAttribute('data-product-id');
            const isTracked = element.getAttribute('data-product-tracked') === 'true';
            
            if (productId && !isTracked && !this.visibleProducts.has(productId)) {
                const rect = element.getBoundingClientRect();
                const isVisible = rect.top >= 0 && rect.left >= 0 && 
                                 rect.bottom <= window.innerHeight && 
                                 rect.right <= window.innerWidth;
                
                if (isVisible) {
                    const productData = this.pendingProducts.get(productId);
                    if (productData) {
                        element.setAttribute('data-product-tracked', 'true');
                        this.visibleProducts.add(productId);
                        const formattedProduct = this.formatProductData(productData);
                        visibleProducts.push(formattedProduct);
                    } else {
                        console.warn(`[GA] trackAllVisibleProducts: Дані товару ${productId} не знайдені в pendingProducts`);
                    }
                }
            }
        });
        
        if (visibleProducts.length > 0) {
            this.sendToDataLayer(visibleProducts);
            this.saveLastCategoryToSessionStorage(visibleProducts);
        }
    }
    /**
     * Зупиняє відстеження
     */
    destroy() {
        if (this.observer) {
            this.observer.disconnect();
        }
        this.reset();
    }
}
// Експортуємо для використання
export default ProductVisibilityTracker;